/**
 * SchoolFaculties — colleges and universities only. The Principal creates
 * Faculties, assigns one Dean each and decides which courses belong where.
 * A Dean landing here sees only their own Faculty.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Plus, Loader2, UserRound, BookOpen, Trash2, Pencil, Library,
} from 'lucide-react';
import SchoolFacultyWorkspace, { FacultyCourseDialog } from './SchoolFacultyWorkspace';

const FacultyDialog = ({ open, onOpenChange, tenantId, editing }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', code: '', description: '' });

  React.useEffect(() => {
    setForm({
      name: editing?.name || '', code: editing?.code || '',
      description: editing?.description || '',
    });
  }, [editing?.id, open]);

  const save = useMutation({
    mutationFn: async (body) => (editing
      ? await api.put(`/school/tenants/${tenantId}/faculties/${editing.id}`, body)
      : await api.post(`/school/tenants/${tenantId}/faculties`, body)).data,
    onSuccess: () => {
      toast.success(editing ? 'Faculty updated' : 'Faculty created');
      queryClient.invalidateQueries({ queryKey: ['school-faculties', tenantId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="faculty-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : 'New Faculty'}</DialogTitle>
          <DialogDescription>e.g. Faculty of Theology, Faculty of Law</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="fac-name">Name *</Label>
            <Input id="fac-name" data-testid="faculty-name-input" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Faculty of Theology" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fac-code">Code</Label>
            <Input id="fac-code" data-testid="faculty-code-input" value={form.code}
                   onChange={(e) => setForm({ ...form, code: e.target.value })}
                   placeholder="THEO" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fac-desc">Description</Label>
            <Input id="fac-desc" data-testid="faculty-description-input" value={form.description}
                   onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="flex justify-end">
            <Button data-testid="faculty-save-btn" disabled={save.isPending || !form.name.trim()}
                    onClick={() => save.mutate(form)}>
              {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Save' : 'Create Faculty'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeanDialog = ({ open, onOpenChange, tenantId, faculty }) => {
  const queryClient = useQueryClient();
  const [pick, setPick] = useState('');
  const [manual, setManual] = useState({ name: '', email: '' });

  const { data: team } = useQuery({
    queryKey: ['school-team', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/team/members`)).data,
    enabled: open,
    retry: false,
  });
  const members = team?.items || team || [];

  const assign = useMutation({
    mutationFn: async (body) => (await api.put(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/dean`, body)).data,
    onSuccess: () => {
      toast.success('Dean assigned');
      queryClient.invalidateQueries({ queryKey: ['school-faculties', tenantId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!faculty) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dean-dialog">
        <DialogHeader>
          <DialogTitle>Dean of {faculty.name}</DialogTitle>
          <DialogDescription>
            One Dean per Faculty. They manage this Faculty's students, courses, timetables and
            results — nothing outside it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Pick a staff member</Label>
            <select value={pick} onChange={(e) => setPick(e.target.value)}
                    data-testid="dean-staff-select"
                    className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">— someone not on the team —</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}{m.position ? ` · ${m.position}` : ''}
                </option>
              ))}
            </select>
          </div>
          {!pick && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={manual.name} data-testid="dean-name-input"
                       onChange={(e) => setManual({ ...manual, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input value={manual.email} data-testid="dean-email-input"
                       onChange={(e) => setManual({ ...manual, email: e.target.value })} />
              </div>
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="ghost" size="sm" data-testid="dean-clear-btn"
                    onClick={() => assign.mutate({ team_member_id: '', name: '', email: '' })}>
              Remove the Dean
            </Button>
            <Button data-testid="dean-save-btn" disabled={assign.isPending}
                    onClick={() => assign.mutate(pick
                      ? { team_member_id: pick }
                      : { name: manual.name, email: manual.email })}>
              {assign.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Assign
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CoursesDialog = ({ open, onOpenChange, tenantId, faculty }) => {
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState([]);

  const { data: progData } = useQuery({
    queryKey: ['school-programmes-all', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/programmes`)).data,
    enabled: open,
    retry: false,
  });
  const programmes = progData?.items || [];

  React.useEffect(() => { setPicked(faculty?.programme_ids || []); }, [faculty?.id, open]);

  const save = useMutation({
    mutationFn: async () => (await api.put(
      `/school/tenants/${tenantId}/faculties/${faculty.id}/programmes`,
      { programme_ids: picked })).data,
    onSuccess: () => {
      toast.success('Courses updated');
      queryClient.invalidateQueries({ queryKey: ['school-faculties', tenantId] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!faculty) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="faculty-courses-dialog">
        <DialogHeader>
          <DialogTitle>Courses in {faculty.name}</DialogTitle>
          <DialogDescription>
            A course belongs to one Faculty — ticking it here moves it out of any other.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {programmes.length === 0 && (
            <p className="text-sm text-slate-500">No programmes exist in this school yet.</p>
          )}
          {programmes.map((p) => {
            const on = picked.includes(p.id);
            return (
              <label key={p.id} data-testid={`faculty-course-${p.id}`}
                     className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm cursor-pointer ${
                       on ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200'}`}>
                <input type="checkbox" checked={on}
                       onChange={() => setPicked(on
                         ? picked.filter((x) => x !== p.id) : [...picked, p.id])} />
                <span className="font-medium text-slate-800">{p.name}</span>
                {p.code && <span className="text-xs text-slate-400 font-mono">{p.code}</span>}
              </label>
            );
          })}
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending}
                  data-testid="faculty-courses-save-btn">
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SchoolFaculties = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deanFor, setDeanFor] = useState(null);
  const [coursesFor, setCoursesFor] = useState(null);
  const [openFaculty, setOpenFaculty] = useState(null);
  const [newCourseFor, setNewCourseFor] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['school-faculties', tenantId],
    queryFn: async () => (await api.get(`/school/tenants/${tenantId}/faculties`)).data,
    retry: false,
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(
      `/school/tenants/${tenantId}/faculties/${id}`)).data,
    onSuccess: () => {
      toast.success('Faculty deleted');
      queryClient.invalidateQueries({ queryKey: ['school-faculties', tenantId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) {
    return (
      <p className="text-sm text-slate-500" data-testid="faculties-not-available">
        Faculties only apply to colleges and universities.
      </p>
    );
  }

  const faculties = data?.items || [];
  const isPrincipal = data?.is_principal;

  return (
    <div className="space-y-4" data-testid="school-faculties">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Faculties</h2>
          <p className="text-sm text-slate-500">
            {isPrincipal
              ? 'Create Faculties, assign a Dean, add courses, subjects and academic departments — all from here.'
              : 'Your Faculty — its students, courses, subjects, departments and results.'}
          </p>
        </div>
        {isPrincipal && (
          <Button onClick={() => { setEditing(null); setShowForm(true); }}
                  data-testid="add-faculty-btn">
            <Plus className="h-4 w-4 mr-2" /> New Faculty
          </Button>
        )}
      </div>

      {faculties.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-500"
                       data-testid="faculties-empty">
            No Faculties yet. Create one for each academic grouping — Theology, Law, Sciences.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {faculties.map((f) => (
            <Card key={f.id} data-testid={`faculty-card-${f.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">{f.name}</CardTitle>
                    <CardDescription>
                      {f.code ? `${f.code} · ` : ''}{f.programmes.length} courses · {f.students} students
                    </CardDescription>
                  </div>
                  {isPrincipal && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" data-testid={`faculty-edit-${f.id}`}
                              onClick={() => { setEditing(f); setShowForm(true); }}>
                        <Pencil className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                      <Button variant="ghost" size="icon" data-testid={`faculty-delete-${f.id}`}
                              onClick={() => remove.mutate(f.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2.5">
                  <span className="h-8 w-8 rounded-full bg-slate-900 grid place-items-center shrink-0">
                    <UserRound className="h-4 w-4 text-white" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate"
                       data-testid={`faculty-dean-${f.id}`}>
                      {f.dean_name || 'No Dean assigned'}
                    </p>
                    {f.dean_email && <p className="text-xs text-slate-400 truncate">{f.dean_email}</p>}
                  </div>
                  {isPrincipal && (
                    <Button variant="outline" size="sm" className="ml-auto shrink-0"
                            data-testid={`faculty-assign-dean-${f.id}`}
                            onClick={() => setDeanFor(f)}>
                      {f.dean_name ? 'Change' : 'Assign Dean'}
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {f.programmes.map((p) => (
                    <Badge key={p.id} variant="outline" className="text-[10px] bg-slate-50">
                      {p.name}
                    </Badge>
                  ))}
                  {f.programmes.length === 0 && (
                    <span className="text-xs text-slate-400">No courses assigned</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  <Button variant="outline" size="sm" data-testid={`faculty-open-${f.id}`}
                          onClick={() => setOpenFaculty(f)}>
                    Open Faculty
                  </Button>
                  {isPrincipal && (
                    <>
                      <Button size="sm" data-testid={`faculty-new-course-${f.id}`}
                              onClick={() => setNewCourseFor(f)}>
                        <Plus className="h-3.5 w-3.5 mr-1.5" /> New Course
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`faculty-courses-${f.id}`}
                              onClick={() => setCoursesFor(f)}>
                        <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Attach courses
                      </Button>
                      <Button variant="ghost" size="sm" data-testid={`faculty-subjects-${f.id}`}
                              onClick={() => setOpenFaculty(f)}>
                        <Library className="h-3.5 w-3.5 mr-1.5" /> Subjects &amp; departments
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <FacultyDialog open={showForm} onOpenChange={setShowForm} tenantId={tenantId}
                     editing={editing} />
      <DeanDialog open={!!deanFor} onOpenChange={(v) => { if (!v) setDeanFor(null); }}
                  tenantId={tenantId} faculty={deanFor} />
      <CoursesDialog open={!!coursesFor} onOpenChange={(v) => { if (!v) setCoursesFor(null); }}
                     tenantId={tenantId} faculty={coursesFor} />
      {openFaculty && (
        <SchoolFacultyWorkspace tenantId={tenantId} faculty={openFaculty}
                                onClose={() => setOpenFaculty(null)}
                                onManageCourses={() => setCoursesFor(openFaculty)} />
      )}
      {newCourseFor && (
        <FacultyCourseDialog open onOpenChange={(v) => { if (!v) setNewCourseFor(null); }}
                             tenantId={tenantId} faculty={newCourseFor} />
      )}
    </div>
  );
};

export default SchoolFaculties;
